import { BoardType } from "@sportactics/board-editing";
import { OfflineError } from "@sportactics/errors";
import { useEffect, useEffectEvent } from "react";
import { useSWRConfig } from "swr";

import { swallow } from "~utils";

import { useUser } from "../user";
import { useBoardFirestorePersistence } from "./use-board-firestore-persistence";
import { getBoardFirestoreSWRKey } from "./use-board-firestore-swr";
import { useBoardLocalPersistence } from "./use-board-local-persistence";
import { getBoardLocalSWRKey } from "./use-board-local-swr";

// Runs once per sign-in: the effect depends on the identity alone, and reads the
// migration through an effect event so a re-render that rebuilds the closure does not
// start a second, overlapping run.
export function useMigrateBoardsEffect(): void {
  const { user } = useUser();
  const migrate = useEffectEvent(useMigrate());

  useEffect(() => {
    if (!user.authenticated) return;

    void swallow(migrate, OfflineError)();
  }, [user.authenticated, user.id]);
}

function useMigrate(): () => Promise<void> {
  const { user } = useUser();
  const { mutate } = useSWRConfig();
  const local = useBoardLocalPersistence();
  const firestore = useBoardFirestorePersistence();

  const userId = user.id;

  return async (): Promise<void> => {
    await Promise.all(
      Object.values(BoardType).map(async (boardType) => {
        if (!(await local.hasBoard(boardType))) return;
        const board = await local.getBoard(boardType);

        if (!board.isEmpty)
          await mutate(
            getBoardFirestoreSWRKey(userId, boardType),
            firestore.upsertBoard(userId, board).then(() => board),
            { optimisticData: board, revalidate: false },
          );

        await mutate(
          getBoardLocalSWRKey(boardType),
          local.deleteBoard(boardType).then(() => null),
          { optimisticData: null, revalidate: false },
        );
      }),
    );
  };
}
